import { TNoteListStyle, TNotificationStyle } from '@/types'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { PRIMARY_COLORS, TPrimaryColor } from '@/constants'
import SecondaryPageLayout from '@/layouts/SecondaryPageLayout'
import { cn } from '@/lib/utils'
import { isSmallScreenAtom } from '@/atoms/screenSize'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { themeSettingAtom, primaryColorAtom, avatarStyleAtom } from '@/atoms/theme'
import {
  enableSingleColumnLayoutSettingAtom,
  notificationListStyleAtom,
  noteListStyleAtom,
  hideNotificationsAtom,
  hideConversationsAtom,
  hideFollowingAtom,
  hideFavoriteRelaysAtom
} from '@/atoms/preferences'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  BellIcon,
  Comment,
  Contact01Icon,
  LayoutTwoColumnIcon,
  ListViewIcon,
  MonitorDotIcon,
  Moon01Icon,
  Note01Icon,
  PanelLeftIcon,
  ServerStack01Icon,
  Sun01Icon
} from '@hugeicons/core-free-icons'
import { forwardRef, ReactNode, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

const THEMES = [
  {
    key: 'system',
    label: 'System',
    icon: <HugeiconsIcon icon={MonitorDotIcon} className="size-5" />
  },
  { key: 'light', label: 'Light', icon: <HugeiconsIcon icon={Sun01Icon} className="size-5" /> },
  { key: 'dark', label: 'Dark', icon: <HugeiconsIcon icon={Moon01Icon} className="size-5" /> }
] as const

const LAYOUTS = [
  {
    key: false,
    label: 'Two-column',
    icon: <HugeiconsIcon icon={LayoutTwoColumnIcon} className="size-5" />
  },
  {
    key: true,
    label: 'Single-column',
    icon: <HugeiconsIcon icon={PanelLeftIcon} className="size-5" />
  }
] as const

const NOTIFICATION_STYLES: { key: TNotificationStyle; label: string; icon: ReactNode }[] = [
  {
    key: 'detailed',
    label: 'Detailed',
    icon: <HugeiconsIcon icon={ListViewIcon} className="size-5" />
  },
  {
    key: 'compact',
    label: 'Compact',
    icon: <HugeiconsIcon icon={ListViewIcon} className="size-5" />
  }
] as const

const NOTE_STYLES: { key: TNoteListStyle; label: string; icon: ReactNode }[] = [
  {
    key: 'full',
    label: 'Full',
    icon: <HugeiconsIcon icon={Note01Icon} className="size-5" />
  },
  {
    key: 'detailed',
    label: 'Detailed',
    icon: <HugeiconsIcon icon={ListViewIcon} className="size-5" />
  },
  {
    key: 'compact',
    label: 'Compact',
    icon: <HugeiconsIcon icon={ListViewIcon} className="size-5" />
  }
] as const

const ThemeSettingsPage = forwardRef(({ index }: { index?: number }, ref) => {
  const { t } = useTranslation()

  useEffect(() => {
    document.title = t('Theme | {{appName}}', { appName: window.hallway.universe.appName })
    history.replaceState(null, '')
  }, [])

  const isSmallScreen = useAtomValue(isSmallScreenAtom)
  const [themeSetting, setThemeSetting] = useAtom(themeSettingAtom)
  const [primaryColor, setPrimaryColor] = useAtom(primaryColorAtom)
  const enableSingleColumnLayout = useAtomValue(enableSingleColumnLayoutSettingAtom)
  const updateEnableSingleColumnLayout = useSetAtom(enableSingleColumnLayoutSettingAtom)
  const notificationListStyle = useAtomValue(notificationListStyleAtom)
  const updateNotificationListStyle = useSetAtom(notificationListStyleAtom)
  const noteListStyle = useAtomValue(noteListStyleAtom)
  const updateNoteListStyle = useSetAtom(noteListStyleAtom)
  const hideNotifications = useAtomValue(hideNotificationsAtom)
  const updateHideNotifications = useSetAtom(hideNotificationsAtom)
  const hideConversations = useAtomValue(hideConversationsAtom)
  const updateHideConversations = useSetAtom(hideConversationsAtom)
  const hideFollowing = useAtomValue(hideFollowingAtom)
  const updateHideFollowing = useSetAtom(hideFollowingAtom)
  const hideFavoriteRelays = useAtomValue(hideFavoriteRelaysAtom)
  const updateHideFavoriteRelays = useSetAtom(hideFavoriteRelaysAtom)
  const [avatarStyle, setAvatarStyle] = useAtom(avatarStyleAtom)

  return (
    <SecondaryPageLayout ref={ref} index={index} title={t('Theme')}>
      <div className="space-y-4 my-3">
        <div className="flex flex-col gap-2 px-4">
          <Label className="text-base">{t('Theme')}</Label>
          <div className="grid grid-cols-3 gap-4 w-full">
            {THEMES.map(({ key, label, icon }) => (
              <OptionButton
                key={key}
                isSelected={themeSetting === key}
                icon={icon}
                label={t(label)}
                onClick={() => setThemeSetting(key)}
              />
            ))}
          </div>
        </div>
        {!isSmallScreen && (
          <div className="flex flex-col gap-2 px-4">
            <Label className="text-base">{t('Layout')}</Label>
            <div className="grid grid-cols-2 gap-4 w-full">
              {LAYOUTS.map(({ key, label, icon }) => (
                <OptionButton
                  key={key.toString()}
                  isSelected={enableSingleColumnLayout === key}
                  icon={icon}
                  label={t(label)}
                  onClick={() => updateEnableSingleColumnLayout(key)}
                />
              ))}
            </div>
          </div>
        )}
        <div className="flex flex-col gap-2 px-4">
          <Label className="text-base">{t('Notification list style')}</Label>
          <div className="grid grid-cols-2 gap-4 w-full">
            {NOTIFICATION_STYLES.map(({ key, label, icon }) => (
              <OptionButton
                key={key}
                isSelected={notificationListStyle === key}
                icon={icon}
                label={t(label)}
                onClick={() => updateNotificationListStyle(key)}
              />
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-2 px-4">
          <Label className="text-base">{t('Normal feed style')}</Label>
          <div className="grid grid-cols-3 gap-4 w-full">
            {NOTE_STYLES.map(({ key, label, icon }) => (
              <OptionButton
                key={key}
                isSelected={noteListStyle === key}
                icon={icon}
                label={t(label)}
                onClick={() => updateNoteListStyle(key)}
              />
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-2 px-4">
          <Label className="text-base">{t('Primary color')}</Label>
          <div className="grid grid-cols-4 gap-4 w-full">
            {Object.entries(PRIMARY_COLORS).map(([key, config]) => (
              <OptionButton
                key={key}
                isSelected={primaryColor === key}
                icon={
                  <div
                    className="size-8 rounded-full shadow-md"
                    style={{
                      backgroundColor: `hsl(${config.light.primary})`
                    }}
                  />
                }
                label={t(config.name)}
                onClick={() => setPrimaryColor(key as TPrimaryColor)}
              />
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-2 px-4">
          <Label className="text-base">{t('Avatar style')}</Label>
          <div className="grid grid-cols-2 gap-4 w-full">
            <OptionButton
              isSelected={avatarStyle === 'circle'}
              icon={<div className="size-8 rounded-full bg-primary" />}
              label={t('Circle')}
              onClick={() => setAvatarStyle('circle')}
            />
            <OptionButton
              isSelected={avatarStyle === 'square'}
              icon={<div className="size-8 rounded-lg bg-primary" />}
              label={t('Square')}
              onClick={() => setAvatarStyle('square')}
            />
          </div>
        </div>
        <div className="flex flex-col gap-3 px-4">
          <Label className="text-base">{t('Hide sections')}</Label>
          <div className="space-y-3">
            <label className="flex items-center justify-between cursor-pointer">
              <div className="flex items-center gap-3">
                <HugeiconsIcon icon={Contact01Icon} className="size-4 text-muted-foreground" />
                <span className="text-sm">{t('Hide Following')}</span>
              </div>
              <Switch checked={hideFollowing} onCheckedChange={updateHideFollowing} />
            </label>
            <label className="flex items-center justify-between cursor-pointer">
              <div className="flex items-center gap-3">
                <HugeiconsIcon icon={Comment} className="size-4 text-muted-foreground" />
                <span className="text-sm">{t('Hide Conversations')}</span>
              </div>
              <Switch checked={hideConversations} onCheckedChange={updateHideConversations} />
            </label>
            <label className="flex items-center justify-between cursor-pointer">
              <div className="flex items-center gap-3">
                <HugeiconsIcon icon={BellIcon} className="size-4 text-muted-foreground" />
                <span className="text-sm">{t('Hide Notifications')}</span>
              </div>
              <Switch checked={hideNotifications} onCheckedChange={updateHideNotifications} />
            </label>
            <label className="flex items-center justify-between cursor-pointer">
              <div className="flex items-center gap-3">
                <HugeiconsIcon icon={ServerStack01Icon} className="size-4 text-muted-foreground" />
                <span className="text-sm">{t('Hide Favorite Relay Feeds')}</span>
              </div>
              <Switch checked={hideFavoriteRelays} onCheckedChange={updateHideFavoriteRelays} />
            </label>
          </div>
        </div>
      </div>
    </SecondaryPageLayout>
  )
})
ThemeSettingsPage.displayName = 'ThemeSettingsPage'
export default ThemeSettingsPage

const OptionButton = ({
  isSelected,
  onClick,
  icon,
  label
}: {
  isSelected: boolean
  onClick: () => void
  icon: React.ReactNode
  label: string
}) => {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex flex-col items-center gap-2 py-4 rounded-lg border-2 transition-all',
        isSelected ? 'border-primary' : 'border-border hover:border-muted-foreground/40'
      )}
    >
      <div className="flex items-center justify-center w-8 h-8">{icon}</div>
      <span className="text-xs font-medium">{label}</span>
    </button>
  )
}
